/** Typed access to VITE_ env vars. Never put secrets in VITE_ vars. */

const DEFAULT_API_BASE_URL = "http://localhost:8787";
// ASSUMPTION: Owner Admin never imports @line/liff directly (LINE SDK
// isolation, ADR-0005/0008 — @line/liff is only ever imported inside
// apps/customer-liff). The QR deep link base is instead a plain configurable
// URL: for local dev it's the customer-liff dev server; for staging/prod ops
// set it to the registered LIFF entry URL (`https://liff.line.me/<LIFF_ID>`),
// the same way `VITE_LIFF_ID` is configured for apps/customer-liff itself
// (docs/architecture/09-cloudflare-deployment.md). Owner Admin never needs to
// know the LIFF ID directly.
const DEFAULT_CUSTOMER_APP_URL = "http://localhost:5273";
const SURFACE_NAME = "Owner Admin";

export type RawApiEnv = {
  VITE_API_BASE_URL?: string;
  VITE_API_MOCK?: string;
  VITE_CUSTOMER_APP_URL?: string;
  DEV?: boolean;
};

export type ApiMode =
  | { kind: "backend"; apiBaseUrl: string }
  | { kind: "mock"; apiBaseUrl: string; reason: "explicit-local-demo" };

export class BackendApiUnavailableError extends Error {
  readonly code = "BACKEND_API_UNAVAILABLE";
  readonly recoverable = true;
  readonly requiredEnv = "VITE_API_BASE_URL";
  readonly demoOverride = "VITE_API_MOCK=1";

  constructor(surfaceName: string) {
    super(
      `${surfaceName} backend API is unavailable; set VITE_API_BASE_URL or enable explicit VITE_API_MOCK=1 for local demo mode.`,
    );
    this.name = "BackendApiUnavailableError";
  }
}

/** Resolve whether this frontend must use backend mode or explicit local mock mode. */
export function resolveApiMode(raw: RawApiEnv): ApiMode {
  const apiBaseUrl = raw.VITE_API_BASE_URL ?? DEFAULT_API_BASE_URL;
  if (raw.VITE_API_MOCK === "1") {
    return { kind: "mock", apiBaseUrl, reason: "explicit-local-demo" };
  }
  return { kind: "backend", apiBaseUrl };
}

/** Backend mode must fail visibly instead of silently falling back to mocks. */
export function assertBackendModeReady(mode: ApiMode, surfaceName = SURFACE_NAME): void {
  if (mode.kind === "backend" && mode.apiBaseUrl.trim().length === 0) {
    throw new BackendApiUnavailableError(surfaceName);
  }
}

export const apiMode = resolveApiMode(import.meta.env);
assertBackendModeReady(apiMode);

export const env = {
  apiBaseUrl: apiMode.apiBaseUrl,
  apiMock: apiMode.kind === "mock",
  apiMode: apiMode.kind,
  isDev: import.meta.env.DEV,
  customerAppUrl: (import.meta.env as RawApiEnv).VITE_CUSTOMER_APP_URL ?? DEFAULT_CUSTOMER_APP_URL,
} as const;
